import { defineStore } from 'pinia';
import { FeatureTogglesServices } from '@/services/store/fuel-finder.service';
import { IFindFuelResponseModel, IFuelStation } from '@/interfaces';
import { ref } from 'vue';
import { PriceIndicatorType, TrendIndicatorType } from '@/enums/fuel-price-trend.enum';
import { provideApolloClient, useApolloClient } from '@vue/apollo-composable';
import FuelBrandLogo from './fuel-brand-logo.query.graphql';
import config from '@/temp/config';
import GraphQLClientFactory from '@/lib/GraphQLClientFactory';

export const useFuelFinderStore = defineStore('FuelFinderStore', () => {
  const fuelStations = ref<IFuelStation[]>([]);
  const lowestPrice = ref<number>(0);
  const averagePrice = ref<number>(0);
  const lastUpdated = ref<Date>();
  const priceState = ref<PriceIndicatorType>();
  const priceTrend = ref<TrendIndicatorType>();
  provideApolloClient(GraphQLClientFactory(config.graphQLEndpoint, true));
  const { resolveClient } = useApolloClient();

  const findFuel = async (lat: number, lng: number, fuelType: string) => {
    const res: IFindFuelResponseModel = await FeatureTogglesServices.findFuel(lat, lng, fuelType);
    fuelStations.value = res.stations ?? [];
    lowestPrice.value = res.lowestPrice ?? 0;
    averagePrice.value = res.averagePrice ?? 0;
    lastUpdated.value = res.lastUpdated;
    priceTrend.value = res.priceIndicator;
    priceState.value = res.priceFluctuation;

    // Fetch brand logo src
    const client = resolveClient();
    const brands = await client.query({
      query: FuelBrandLogo,
    });
    const defaultSrc = brands.data.item.children.find(
      (item: { name: string; url: string }) => item.name == 'Default'
    )?.url;
    fuelStations.value.forEach((site) => {
      const url = brands.data.item.children.find((item: { name: string; url: string }) => item.name == site.brand)?.url;
      site.brandLogoSrc = `${process.env.VUE_APP_SITECORE_API_HOST}/${url ?? defaultSrc}`;
    });

    fuelStations.value.sort((a, b) => {
      if (a.price == b.price) return b.discount - a.discount; // RACQ partners show on top
      return a.price - b.price;
    });
  };

  return {
    fuelStations,
    lowestPrice,
    averagePrice,
    lastUpdated,
    priceTrend,
    priceState,
    findFuel,
  };
});
